import { Box, Button, Container, Typography, styled } from '@mui/material';
import React from 'react';

import { PublicRoutes } from '@/config/routes';

const HeroWrapper = styled(Box)(({ theme }) => ({
  backgroundImage: 'url("/assets/images/landing/pm2.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  paddingTop: '80px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 0,
  },
}));

const HeroContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  maxWidth: '1000px',
  margin: '0 auto',
  padding: theme.spacing(4, 2),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6, 4),
  },
}));

const CTAButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

const PrimaryButtonRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '14px 32px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '1.5',
  color: '#FFFFFF',
  backgroundColor: '#C1170B',
  boxShadow: '0 4px 12px rgba(193, 23, 11, 0.3)',
  border: 'none',
  minWidth: '160px',
  '&:hover': {
    backgroundColor: '#9E1309',
    boxShadow: '0 6px 16px rgba(193, 23, 11, 0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '14px 32px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '1.5',
  color: '#414651',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid #E5E7EB',
  minWidth: '160px',
  '&:hover': {
    backgroundColor: '#F9FAFB',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

interface PMHeroSectionProps {
  scrollToFeatures?: () => void;
  scrollToSecurity?: () => void;
  campaign?: string;
}

const PMHeroSection: React.FC<PMHeroSectionProps> = ({
  scrollToFeatures,
  scrollToSecurity,
  campaign,
}) => {
  const getAuthUrl = (route: string) => (campaign ? `${route}?campaign=${campaign}` : route);

  return (
    <HeroWrapper>
      <Container maxWidth="lg">
        <HeroContent>
          <Typography
            variant="h1"
            sx={{
              lineHeight: 1.2,
              mb: 3,
              textAlign: 'center',
              color: '#FFFFFF',
            }}
          >
            <Box
              component="span"
              sx={{
                fontWeight: 200,
                fontSize: { xs: '34px', md: '56px', lg: '75px' },
                display: 'block',
                mt: 1,
                color: '#FFFFFF',
                fontFamily: 'Nohemi',
              }}
            >
              <strong>
                Smarter Real Property
                <br />
                Powered by AI
              </strong>
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: '#FFFFFF',
              maxWidth: '600px',
              fontWeight: 700,
              lineHeight: 1.6,
              textAlign: 'center',
              fontSize: { xs: '16px', md: '18px', lg: '20px' },
            }}
          >
            Let Terr.ai help you make sense of everything, from document processing to automated
            calendar management. Whether you own your home, rent an apartment, manage an HOA, live
            in a dorm, or have commercial property, etc, etc - we've got you covered - for free.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 4,
              color: '#FFFFFF',
              maxWidth: '600px',
              lineHeight: 1.6,
              textAlign: 'center',
              fontSize: { xs: '16px', md: '18px' },
              opacity: 0.9,
            }}
          >
            <b style={{ fontWeight: 700 }}>
              <u>We take security seriously</u>
            </b>{' '}
            and use industry-standard practices to help protect your property information. Learn
            more about our security measures below.
          </Typography>
          <CTAButtonGroup>
            <PrimaryButtonRow>
              <SecondaryButton variant="outlined" href={getAuthUrl(PublicRoutes.login)}>
                Login
              </SecondaryButton>
              <PrimaryButton
                variant="contained"
                disableElevation
                href={getAuthUrl(PublicRoutes.signup)}
              >
                Get Started
              </PrimaryButton>
              <SecondaryButton variant="outlined" onClick={scrollToSecurity}>
                Security & Privacy
              </SecondaryButton>
            </PrimaryButtonRow>
          </CTAButtonGroup>
        </HeroContent>
      </Container>
    </HeroWrapper>
  );
};

export default PMHeroSection;
